import {
  Image,
  StyleSheet,
  useColorScheme,
  View,
  type LayoutChangeEvent,
} from "react-native";

import { getAppColors } from "@/theme/app-theme";

// The launch artwork, shared with the NATIVE splash screen — `expo-splash-screen`
// in app.json draws the same picture, and the native projects hold their own
// copies of it (iOS imageset, Android drawables). Drawing the identical picture
// on both sides is what makes the handover from native to JS invisible: nothing
// resizes, moves, appears or changes colour, so the two screens read as one.
// `MARK_SIZE` must stay in step with `imageWidth` in app.json and `CANVAS` in
// the generator. Each require resolves to a 1x/2x/3x set.
//
// Regenerate all of it with `npm run splash:generate`.
const SPLASH_MARK = require("../../../assets/images/splash-mark.png");
const SPLASH_MARK_DARK = require("../../../assets/images/splash-mark-dark.png");
const MARK_SIZE = 176;

export type SplashViewProps = {
  onLayout?: (event: LayoutChangeEvent) => void;
};

/**
 * The launch screen, in React.
 *
 * Deliberately inert: no spinner, no label, no animation. Anything this screen
 * shows that the native splash cannot show turns one launch screen into two —
 * the user watches the app apparently reach a second, different waiting state
 * on the way up. Waiting states that the user is meant to read belong further
 * in, where they can say what is actually being waited for.
 */
export function SplashView({ onLayout }: SplashViewProps) {
  // The device scheme, NOT the in-app theme preference. This screen has to
  // match the native splash it is taking over from, and the native side has no
  // access to the preference — Android picks `values-night` and iOS picks the
  // dark imageset straight from the OS. A user who has forced light inside a
  // dark phone would otherwise get a dark native splash handing over to a white
  // JS one, which is the seam this whole arrangement exists to remove. The
  // app's own theme takes over at the first real screen.
  const isDark = useColorScheme() === "dark";
  const colors = getAppColors(isDark ? "dark" : "light");

  return (
    <View
      onLayout={onLayout}
      style={[styles.splash, { backgroundColor: colors.background }]}
    >
      {/*
        Centred in the window, exactly as the native splash centres it. Do not
        wrap this in a layout that shifts it — the point is that it does not
        move when the native screen hands over.
      */}
      <Image
        source={isDark ? SPLASH_MARK_DARK : SPLASH_MARK}
        style={styles.mark}
        resizeMode="contain"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  splash: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  mark: {
    width: MARK_SIZE,
    height: MARK_SIZE,
  },
});
